/**
 * Featured Collection Bundles Section
 * Handles slider, filters, view toggle, and drawer functionality.
 * Also runs drawer setup for bundle cards in #product-grid (collection page).
 * Sync verified: 2026-01-29
 */

(function() {
  'use strict';

  const DRAWER_MOBILE_BREAKPOINT = 767;

  function trackGaEvent(eventName, payload) {
    if (window.Shopify?.designMode) return;
    if (window.jrsTrackEvent) {
      window.jrsTrackEvent(eventName, payload, { prefix: '' });
      return;
    }
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      try {
        window.dataLayer.push({
          event: eventName,
          ...payload
        });
      } catch (e) {
        console.error('[JRS-GA4]', e);
      }
    }
  }

  function isMobile() {
    return window.innerWidth <= DRAWER_MOBILE_BREAKPOINT;
  }

  function getDrawerVisibleOpacity(drawer) {
    if (!drawer) return '1';
    const value = drawer.dataset ? drawer.dataset.visibleOpacity : null;
    const parsed = value !== undefined ? parseFloat(value) : NaN;
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 1.5) {
      return parsed.toString();
    }
    return '1';
  }

  var closeAllDrawersModule = function closeAllDrawers() {
    document.querySelectorAll('.bundle-card-drawer--open').forEach(drawer => {
      drawer.classList.remove('bundle-card-drawer--open');
      drawer.style.opacity = '0';
      drawer.style.display = 'none';
      drawer.style.pointerEvents = 'none';
      drawer.style.transform = '';
      drawer.style.transition = '';
      drawer.removeEventListener('touchstart', drawer._swipeTouchStart);
      drawer.removeEventListener('touchmove', drawer._swipeTouchMove);
      drawer.removeEventListener('touchend', drawer._swipeTouchEnd);
      delete drawer._swipeTouchStart;
      delete drawer._swipeTouchMove;
      delete drawer._swipeTouchEnd;
    });
  }

  function setupDrawerSwipeToClose(drawer) {
    if (!drawer || !isMobile()) return;
    if (drawer._swipeTouchStart) {
      drawer.removeEventListener('touchstart', drawer._swipeTouchStart);
      drawer.removeEventListener('touchmove', drawer._swipeTouchMove);
      drawer.removeEventListener('touchend', drawer._swipeTouchEnd);
    }
    const swipeThreshold = 80;
    let touchStartY = 0;
    let isSwipeClosing = false;
    const scrollEl = drawer;

    const handleTouchStart = (e) => {
      if (e.target.closest('button, a, input, select, textarea')) {
        isSwipeClosing = false;
        return;
      }
      touchStartY = e.touches[0].clientY;
      isSwipeClosing = false;
    };

    const handleTouchMove = (e) => {
      if (!isSwipeClosing && scrollEl.scrollTop > 0) return;
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - touchStartY;
      const isAtTop = scrollEl.scrollTop <= 0;
      if (isAtTop && deltaY > 0) {
        e.preventDefault();
        isSwipeClosing = true;
        const progress = Math.min(deltaY / (swipeThreshold * 2), 1);
        drawer.style.transform = `translateY(${deltaY * 0.5}px) scale(${1 - progress * 0.03})`;
        drawer.style.transition = 'none';
      } else if (isSwipeClosing && deltaY <= 0) {
        isSwipeClosing = false;
        drawer.style.transition = '';
        drawer.style.transform = '';
      }
    };

    const handleTouchEnd = (e) => {
      if (!isSwipeClosing) return;
      const currentY = e.changedTouches[0].clientY;
      const deltaY = currentY - touchStartY;
      drawer.style.transition = '';
      drawer.style.transform = '';
      if (deltaY > swipeThreshold) {
        var cardEl = drawer.closest('.bundle-product-card');
        var productId = cardEl ? cardEl.getAttribute('data-product-id') || '' : '';
        closeAllDrawersModule();
        if (window.jrsTrackEvent && productId) {
          window.jrsTrackEvent('bundle_drawer_close', {
            product_id: productId,
            close_method: 'swipe',
            device_type: 'mobile'
          }, { prefix: '' });
        }
      }
      isSwipeClosing = false;
    };

    drawer._swipeTouchStart = handleTouchStart;
    drawer._swipeTouchMove = handleTouchMove;
    drawer._swipeTouchEnd = handleTouchEnd;
    drawer.addEventListener('touchstart', handleTouchStart, { passive: true });
    drawer.addEventListener('touchmove', handleTouchMove, { passive: false });
    drawer.addEventListener('touchend', handleTouchEnd, { passive: true });

    drawer.querySelectorAll('[data-bundle-drawer-close]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        var cardEl = drawer.closest('.bundle-product-card');
        var productId = cardEl ? cardEl.getAttribute('data-product-id') || '' : '';
        closeAllDrawersModule();
        if (window.jrsTrackEvent && productId) {
          window.jrsTrackEvent('bundle_drawer_close', {
            product_id: productId,
            close_method: 'button'
          }, { prefix: '' });
        }
      });
    });
  }

  function setupDrawersForCards(cards, options) {
    const opts = options || {};
    const animationDuration = opts.animationDuration || 300;
    const shouldAnimate = opts.shouldAnimate !== false;
    const mobile = opts.isMobile !== undefined ? opts.isMobile : isMobile();

    cards.forEach(card => {
      if (card.dataset.drawerBound === 'true') return;
      const drawer = card.querySelector('.bundle-card-drawer');
      if (!drawer) return;

      if (!mobile) {
        card.addEventListener('mouseenter', () => {
          if (shouldAnimate) {
            drawer.style.transition = `opacity ${animationDuration}ms ease-out, transform ${animationDuration}ms ease-out`;
          }
          drawer.style.opacity = getDrawerVisibleOpacity(drawer);
          drawer.style.transform = 'translateY(0)';
          drawer.style.pointerEvents = 'auto';
        });
        card.addEventListener('mouseleave', () => {
          if (shouldAnimate) {
            drawer.style.transition = `opacity ${animationDuration}ms ease-out, transform ${animationDuration}ms ease-out`;
          }
          drawer.style.opacity = '0';
          drawer.style.transform = 'translateY(10px)';
          drawer.style.pointerEvents = 'none';
        });
      } else {
        const trigger = card.querySelector('[data-drawer-trigger]');
        if (trigger) {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const isOpen = drawer.classList.contains('bundle-card-drawer--open');
            closeAllDrawersModule();
            if (!isOpen) {
              drawer.classList.add('bundle-card-drawer--open');
              drawer.style.display = 'block';
              drawer.style.pointerEvents = 'auto';
              if (shouldAnimate) {
                drawer.style.transition = `opacity ${animationDuration}ms ease-out, transform ${animationDuration}ms ease-out`;
              }
              drawer.style.opacity = getDrawerVisibleOpacity(drawer);
              drawer.style.transform = 'translateY(0)';
              setupDrawerSwipeToClose(drawer);
              if (window.jrsTrackEvent) {
                var section = card.closest('.featured-collection-bundles');
                var allCards = section ? section.querySelectorAll('.bundle-product-card') : [];
                var position = section ? Array.prototype.indexOf.call(allCards, card) : -1;
                var productId = card.getAttribute('data-product-id') || trigger.getAttribute('data-drawer-trigger') || '';
                var titleEl = card.querySelector('.bundle-product-card__title');
                var itemName = titleEl ? titleEl.textContent.trim() : '';
                window.jrsTrackEvent('bundle_drawer_open', {
                  item_name: itemName,
                  item_position: position,
                  product_id: productId,
                  device_type: 'mobile',
                  trigger: 'button'
                }, { prefix: '' });
              }
            }
          });
        }
      }
      card.dataset.drawerBound = 'true';
    });
  }

  function initBundleCardDrawersForGrid() {
    const grid = document.getElementById('product-grid') || document.querySelector('[data-products-grid]');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('.bundle-product-card'));
    if (cards.length === 0) return;
    setupDrawersForCards(cards, {
      animationDuration: 250,
      shouldAnimate: true,
      isMobile: isMobile()
    });
  }

  window.initBundleCardDrawers = function() {
    initBundleCardDrawersForGrid();
  };

  class FeaturedCollectionBundles {
    constructor(section) {
      if (section.dataset.bundleSectionInitialized === 'true') return;
      this.section = section;
      this.sectionId = section.dataset.sectionId || '';
      this.collectionHandle = section.dataset.collectionHandle || '';
      this.sliderTrack = section.querySelector('[data-slider-track]');
      this.sliderContainer = section.querySelector('[data-slider-container]');
      this.cards = Array.from(section.querySelectorAll('.bundle-product-card'));
      this.arrows = {
        prev: section.querySelector('.featured-collection-bundles__nav-arrow--prev'),
        next: section.querySelector('.featured-collection-bundles__nav-arrow--next')
      };
      this.dotsContainer = section.querySelector('[data-dots-container]');
      this.filterTabs = Array.from(section.querySelectorAll('.bundle-filter-tabs__tab'));
      this.viewToggle = section.querySelector('[data-view-toggle]');
      this.viewToggleButtons = section.querySelectorAll('.bundle-view-toggle__button');
      
      // Settings
      this.settings = this.getSettings();
      this.animationsEnabled = section.dataset.animationsEnabled === 'true';
      this.respectReducedMotion = section.dataset.respectReducedMotion === 'true';
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.shouldAnimate = this.animationsEnabled && !(this.respectReducedMotion && this.prefersReducedMotion);
      
      // State
      this.currentSlide = 0;
      this.currentFilter = 'all';
      this.currentView = this.getInitialView();
      this.filteredCards = [...this.cards];
      this.autoplayTimer = null;
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.scrollLeft = 0;
      this.lastIsMobile = false;
      this._sliderTrackDebounceTimer = null;
      this._sliderTrackDebouncePayload = null;
      
      // Breakpoints
      this.breakpoints = {
        mobile: 767,
        tablet: 1023,
        desktop: 1279
      };
      
      this.init();
    }

    getSettings() {
      // Get settings from data attributes or defaults
      return {
        cardsDesktop: parseInt(this.section.dataset.cardsDesktop) || 4,
        cardsTablet: parseInt(this.section.dataset.cardsTablet) || 3,
        cardsMobile: parseInt(this.section.dataset.cardsMobile) || 2,
        enablePeek: this.section.dataset.enablePeek !== 'false',
        peekAmount: parseInt(this.section.dataset.peekAmount) || 10,
        cardGapDesktop: parseInt(this.section.dataset.cardGapDesktop) || 24,
        cardGapTablet: parseInt(this.section.dataset.cardGapTablet) || 20,
        cardGapMobile: parseInt(this.section.dataset.cardGapMobile) || 16,
        enableLoop: this.section.dataset.enableLoop === 'true',
        enableAutoplay: this.section.dataset.enableAutoplay === 'true',
        autoplayInterval: parseInt(this.section.dataset.autoplayInterval) || 5000,
        autoplayPauseHover: this.section.dataset.autoplayPauseHover !== 'false',
        animationDuration: parseInt(this.section.dataset.animationDuration) || 300,
        arrowPosition: this.section.dataset.arrowPosition || 'outside',
        dotStyle: this.section.dataset.dotStyle || 'circles',
        defaultMobileView: this.section.dataset.defaultMobileView || '4'
      };
    }

    /**
     * Normalize bundle card heights so all visible cards in the slider
     * have the same height. This fixes uneven card heights when titles,
     * badges, or meta lines wrap differently.
     */
    updateCardHeights() {
      if (!this.cards || this.cards.length === 0) return;

      // Reset any inline heights first so measurements are accurate
      this.cards.forEach(function(card) {
        card.style.height = '';
      });

      var maxHeight = 0;
      this.cards.forEach(function(card) {
        var h = card.offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      if (!maxHeight) return;

      this.cards.forEach(function(card) {
        card.style.height = maxHeight + 'px';
      });
    }

    _pushDebouncedSliderEvent(eventName, params) {
      const self = this;
      if (this._sliderTrackDebounceTimer) clearTimeout(this._sliderTrackDebounceTimer);
      this._sliderTrackDebouncePayload = { event: eventName, params: params };
      this._sliderTrackDebounceTimer = setTimeout(function() {
        if (self._sliderTrackDebouncePayload) {
          trackGaEvent(self._sliderTrackDebouncePayload.event, self._sliderTrackDebouncePayload.params);
        }
        self._sliderTrackDebouncePayload = null;
        self._sliderTrackDebounceTimer = null;
      }, 300);
    }

    getInitialView() {
      // Check localStorage first
      const savedView = localStorage.getItem('jrs_collection_view_preference');
      if (savedView && ['1', '2', '4'].includes(savedView)) {
        return savedView;
      }
      return this.settings.defaultMobileView || '4';
    }

    /**
     * Normalize bundle card heights so all visible cards in the slider
     * have the same height. This fixes uneven card heights when titles,
     * badges, or meta lines wrap differently.
     */
    updateCardHeights() {
      if (!this.cards || this.cards.length === 0) return;

      // Reset any inline heights first so measurements are accurate
      this.cards.forEach(function(card) {
        card.style.height = '';
      });

      var maxHeight = 0;
      this.cards.forEach(function(card) {
        var h = card.offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      if (!maxHeight) return;

      this.cards.forEach(function(card) {
        card.style.height = maxHeight + 'px';
      });
    }

    init() {
      // Only use transform mode for Safari (not Chrome, Edge, Firefox, etc.)
      // Regex: matches "safari" but NOT if "chrome" or "android" appears before it
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      this.useTransformMode = isSafari;
      
      if (this.useTransformMode) {
        this.section.classList.add('featured-collection-bundles--transform-mode');
      }

      if (!this.sliderContainer || this.cards.length === 0) {
        return;
      }

      // Initialize lastIsMobile after window is ready
      this.lastIsMobile = this.isMobile();

      this.setupEventListeners();
      this.setupFilters();
      this.setupViewToggle();
      this.setupDrawers();
      this.setupSlider();
      this.updateDots();
      this.updateArrows();
      this.updateFilterCounts(0, 999); // Initial filter counts

      // Equalize card heights after initial layout
      this.updateCardHeights();

      // Recalculate heights on resize (debounced)
      var self = this;
      if (!this._cardHeightResizeBound) {
        this._cardHeightResizeBound = true;
        window.addEventListener('resize', function() {
          clearTimeout(self._cardHeightResizeTimer);
          self._cardHeightResizeTimer = setTimeout(function() {
            try {
              if (typeof self.updateCardHeights === 'function') self.updateCardHeights();
            } catch (cardHeightErr) {
              console.warn('[FeaturedBundles] updateCardHeights on resize error:', cardHeightErr && cardHeightErr.message ? cardHeightErr.message : cardHeightErr);
            }
          }, 150);
        });
      }

      // Consolidate updateSlider calls: once on load, optionally once more for mobile views 1/2
      window.addEventListener('load', () => {
        this.updateSlider();
        this.updateDots();
        this.updateArrows();
        this.updateCardHeights();
        // On mobile with saved view 1 or 2, re-run after layout settles
        if (this.isMobile() && ['1', '2'].includes(this.currentView)) {
          setTimeout(() => {
            this.updateSlider();
            this.updateDots();
            this.updateArrows();
            this.updateCardHeights();
          }, 150);
        }
      }, { once: true });
      
      if (this.settings.enableAutoplay) {
        this.startAutoplay();
      }

      this.section.dataset.bundleSectionInitialized = 'true';
      this.section._bundlesInstance = this;

      var sectionId = this.sectionId;
      var collectionHandle = this.collectionHandle;

      // View All link click
      var viewAllLink = this.section.querySelector('.featured-collection-bundles__view-all-link');
      if (viewAllLink) {
        viewAllLink.addEventListener('click', function() {
          if (window.jrsTrackEvent) {
            window.jrsTrackEvent('bundle_view_all_click', {
              section_id: sectionId,
              collection_handle: collectionHandle,
              destination_url: viewAllLink.getAttribute('href') || ''
            }, { prefix: '' });
          }
        });
      }

      // Header CTA and empty state CTA (delegated for dynamically added empty state)
      this.section.addEventListener('click', function(e) {
        var cta = e.target.closest('.bundle-header-cta');
        if (cta && window.jrsTrackEvent) {
          window.jrsTrackEvent('bundle_header_cta_click', {
            cta_text: (cta.textContent || '').trim(),
            destination_url: cta.getAttribute('href') || '',
            section_id: sectionId
          }, { prefix: '' });
          return;
        }
        var emptyBtn = e.target.closest('.bundle-empty-state__button');
        if (emptyBtn && window.jrsTrackEvent) {
          var inst = e.currentTarget._bundlesInstance;
          window.jrsTrackEvent('bundle_empty_state_click', {
            cta_text: (emptyBtn.textContent || '').trim(),
            destination_url: emptyBtn.getAttribute('href') || '',
            active_filter: inst ? inst.currentFilter : 'all'
          }, { prefix: '' });
        }
      });

      // Bundle product card link click
      this.section.querySelectorAll('.bundle-product-card__link').forEach(function(link, i) {
        link.addEventListener('click', function() {
          const card = link.closest('.bundle-product-card');
          if (!card) return;
          const productId = card.getAttribute('data-product-id') || '';
          const teamSize = card.getAttribute('data-team-size') || '';
          const titleEl = card.querySelector('.bundle-product-card__title');
          const fallbackTitle = titleEl ? titleEl.textContent.trim() : '';
          const itemName = card.dataset.productTitle || fallbackTitle || 'unknown';
          const priceEl = card.querySelector('.bundle-product-card__price .price.in-vat');
          const price = priceEl ? priceEl.textContent.trim() : '';
          const payload = {
            item_name: itemName,
            item_position: i,
            product_id: productId,
            product_url: link.getAttribute('href') || '',
            price: price,
            team_size: teamSize,
            section_id: sectionId
          };
          trackGaEvent('bundle_product_click', payload);
        });
      });
    }

    setupEventListeners() {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const self = this;

      if (this.arrows.prev) {
        this.arrows.prev.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!this.arrows.prev.disabled) {
            const fromSlide = this.currentSlide;
            this.goToSlide(fromSlide - 1);
            if (window.jrsTrackEvent) {
              window.jrsTrackEvent('bundle_slider_arrow_click', {
                direction: 'prev',
                from_slide: fromSlide,
                to_slide: this.currentSlide,
                section_id: this.sectionId,
                collection_handle: this.collectionHandle
              }, { prefix: '' });
            }
          }
        }, { passive: false });
      }
      
      if (this.arrows.next) {
        this.arrows.next.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!this.arrows.next.disabled) {
            const fromSlide = this.currentSlide;
            this.goToSlide(fromSlide + 1);
            if (window.jrsTrackEvent) {
              window.jrsTrackEvent('bundle_slider_arrow_click', {
                direction: 'next',
                from_slide: fromSlide,
                to_slide: this.currentSlide,
                section_id: this.sectionId,
                collection_handle: this.collectionHandle
              }, { prefix: '' });
            }
          }
        }, { passive: false });
      }

      // Keyboard navigation
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goToSlide(this.currentSlide - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goToSlide(this.currentSlide + 1);
        }
      });

      // Touch/swipe support
      this.setupTouchEvents();
      this.setupMouseDragEvents();
      
      // Sync currentSlide with scroll position
      this.setupScrollSync();

      // Resize handler (wrapped for workwear-bundles mobile menu crash debugging)
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          try {
            // Check if we switched between mobile/desktop
            const wasMobile = this.lastIsMobile;
            const nowMobile = this.isMobile();
            
            if (wasMobile !== nowMobile) {
              if (!nowMobile) {
                // Switching from mobile to desktop - unwrap view 4 and reset styles
                if (typeof this.unwrapView4Cards === 'function') this.unwrapView4Cards();
                if (this.sliderContainer) {
                  this.sliderContainer.style.display = '';
                  this.sliderContainer.style.gridTemplateColumns = '';
                }
                if (this.filteredCards && this.filteredCards.length) {
                  this.filteredCards.forEach(card => {
                    if (card && card.style) {
                      card.style.flex = '';
                      card.style.minWidth = '';
                      card.style.maxWidth = '';
                    }
                  });
                }
                if (this.arrows && this.arrows.prev) this.arrows.prev.style.display = '';
                if (this.arrows && this.arrows.next) this.arrows.next.style.display = '';
                if (this.dotsContainer) this.dotsContainer.style.display = '';
              } else {
                if (typeof this.applyView === 'function') this.applyView(this.currentView);
              }
            }
            
            this.lastIsMobile = nowMobile;
            if (typeof this.updateSlider === 'function') this.updateSlider();
            if (typeof this.updateDots === 'function') this.updateDots();
            if (typeof this.updateArrows === 'function') this.updateArrows();
          } catch (resizeErr) {
            console.warn('[FeaturedBundles] resize handler error:', resizeErr && resizeErr.message ? resizeErr.message : resizeErr);
          }
        }, 250);
      });

      // On mobile with view 1/2, re-measure when visual viewport changes (e.g. iOS Chrome orientation)
      if (typeof window.visualViewport !== 'undefined') {
        let vvTimer;
        window.visualViewport.addEventListener('resize', () => {
          if (!this.isMobile() || !['1', '2'].includes(this.currentView)) return;
          clearTimeout(vvTimer);
          vvTimer = setTimeout(() => {
            try {
              if (typeof this.updateSlider === 'function') this.updateSlider();
              if (typeof this.updateDots === 'function') this.updateDots();
              if (typeof this.updateArrows === 'function') this.updateArrows();
            } catch (vvErr) {
              console.warn('[FeaturedBundles] visualViewport resize error:', vvErr && vvErr.message ? vvErr.message : vvErr);
            }
          }, 100);
        });
      }

      // Autoplay pause on hover
      if (this.settings.enableAutoplay && this.settings.autoplayPauseHover) {
        this.sliderTrack.addEventListener('mouseenter', () => this.stopAutoplay());
        this.sliderTrack.addEventListener('mouseleave', () => {
          if (this.settings.enableAutoplay) {
            this.startAutoplay();
          }
        });
      }
    }

    setupTouchEvents() {
      if (!this.sliderTrack) return;
      
      let touchStartX = 0;
      let touchStartScrollLeft = 0;
      let touchStartTransform = 0;
      let isDragging = false;
      let startTime = 0;
      const useTransform = this.useTransformMode;
      const useCapture = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      const handleTouchStart = (e) => {
        const target = e.target;
        const isInteractive = target.closest('button, a, input, select, textarea, [data-drawer-trigger], .bundle-card-drawer');
        if (isInteractive) {
          isDragging = false;
          return;
        }
        
        isDragging = true;
        touchStartX = e.touches[0].clientX;
        if (useTransform && (this._transformPosition !== undefined)) {
          touchStartTransform = this._transformPosition;
        } else {
          touchStartScrollLeft = this.sliderTrack.scrollLeft;
          this.sliderTrack.style.scrollBehavior = 'auto';
        }
        startTime = Date.now();
      };

      const handleTouchMove = (e) => {
        if (!isDragging) return;
        const currentX = e.touches[0].clientX;
        const diffX = touchStartX - currentX;
        
        if (useTransform && this._slideWidth !== undefined && this._maxSlide !== undefined) {
          const maxTransform = this._maxSlide * this._slideWidth;
          const newTransform = Math.max(0, Math.min(touchStartTransform + diffX, maxTransform));
          this.sliderContainer.style.transition = 'none';
          this.sliderContainer.style.transform = 'translate3d(-' + newTransform + 'px, 0, 0)';
          this.sliderContainer.style.webkitTransform = 'translate3d(-' + newTransform + 'px, 0, 0)';
        } else {
          this.sliderTrack.scrollLeft = touchStartScrollLeft + diffX;
        }
      };

      const handleTouchEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        const currentX = e.changedTouches[0].clientX;
        const diffX = touchStartX - currentX;
        const duration = Math.max(Date.now() - startTime, 1);
        const velocity = Math.abs(diffX) / duration;
        
        if (useTransform && this._slideWidth !== undefined && this._maxSlide !== undefined) {
          this.sliderContainer.style.transition = '';
          const currentTransform = touchStartTransform + diffX;
          const slideIndex = Math.round(currentTransform / this._slideWidth);
          const clamped = Math.max(0, Math.min(slideIndex, this._maxSlide));
          const threshold = this._slideWidth * 0.25;
          const shouldChange = Math.abs(diffX) > threshold || velocity > 0.3;
          const fromSlide = this.currentSlide;
          if (shouldChange) {
            if (diffX > 0) {
              this.goToSlide(Math.min(this.currentSlide + 1, this._maxSlide));
            } else {
              this.goToSlide(Math.max(this.currentSlide - 1, 0));
            }
          } else {
            this.goToSlide(clamped);
          }
          if (fromSlide !== this.currentSlide) {
            this._pushDebouncedSliderEvent('bundle_slider_swipe', {
              direction: diffX > 0 ? 'right' : 'left',
              from_slide: fromSlide,
              to_slide: this.currentSlide,
              device_type: 'mobile'
            });
          }
          return;
        }
        
        this.sliderTrack.style.scrollBehavior = '';
        const containerWidth = this.getSliderWidth();
        if (containerWidth <= 0) return;
        
        const isMobile = this.isMobile();
        const isTablet = this.isTablet();
        const isView4 = isMobile && parseInt(this.currentView) === 4;
        const wrappers = this.sliderContainer.querySelectorAll('.bundle-view-4-wrapper');
        let slideWidth, maxSlide;
        
        if (isView4 && wrappers.length > 0) {
          const gap = this.settings.cardGapMobile;
          slideWidth = containerWidth + gap;
          maxSlide = Math.max(0, wrappers.length - 1);
        } else {
          let cardsVisible = isMobile ? parseInt(this.currentView) : isTablet ? this.settings.cardsTablet : this.settings.cardsDesktop;
          let gap = isMobile ? this.settings.cardGapMobile : isTablet ? this.settings.cardGapTablet : this.settings.cardGapDesktop;
          if (isMobile && cardsVisible === 2) gap = 8;
          const totalGaps = (cardsVisible - 1) * gap;
          const availableWidth = containerWidth - totalGaps;
          const cardWidth = availableWidth / cardsVisible;
          slideWidth = cardWidth + gap;
          maxSlide = Math.max(0, this.filteredCards.length - cardsVisible);
        }
        
        const currentScrollLeft = this.sliderTrack.scrollLeft;
        const calculatedSlide = Math.round(currentScrollLeft / slideWidth);
        const clampedSlide = Math.max(0, Math.min(calculatedSlide, maxSlide));
        const threshold = slideWidth * 0.25;
        const shouldChangeSlide = Math.abs(diffX) > threshold || velocity > 0.3;
        const fromSlide = this.currentSlide;
        if (shouldChangeSlide) {
          if (diffX > 0) {
            this.goToSlide(Math.min(this.currentSlide + 1, maxSlide));
          } else {
            this.goToSlide(Math.max(this.currentSlide - 1, 0));
          }
        } else {
          this.goToSlide(clampedSlide);
        }
        if (fromSlide !== this.currentSlide) {
          this._pushDebouncedSliderEvent('bundle_slider_swipe', {
            direction: diffX > 0 ? 'right' : 'left',
            from_slide: fromSlide,
            to_slide: this.currentSlide,
            device_type: 'mobile'
          });
        }
      };

      // Safari-specific: Add event listeners with appropriate options
      this.sliderTrack.addEventListener('touchstart', handleTouchStart, { 
        passive: true,
        capture: useCapture 
      });
      this.sliderTrack.addEventListener('touchmove', handleTouchMove, { 
        passive: true,
        capture: useCapture 
      });
      this.sliderTrack.addEventListener('touchend', handleTouchEnd, { 
        passive: true,
        capture: useCapture 
      });
    }

    setupScrollSync() {
      if (!this.sliderTrack) return;
      
      let scrollTimeout;
      const self = this;
      this.sliderTrack.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          try {
            self.syncSlideFromScroll();
          } catch (scrollErr) {
            console.warn('[FeaturedBundles] syncSlideFromScroll error:', scrollErr && scrollErr.message ? scrollErr.message : scrollErr);
          }
        }, 50);
      }, { passive: true });
    }
    
    syncSlideFromScroll() {
      if (this.useTransformMode || !this.sliderTrack || this.filteredCards.length === 0) return;
      
      const containerWidth = this.getSliderWidth();
      if (containerWidth <= 0) return;
      
      const isMobile = this.isMobile();
      const isTablet = this.isTablet();
      
      // Check if we're in view 4 mode
      const isView4 = isMobile && parseInt(this.currentView) === 4;
      const wrappers = this.sliderContainer.querySelectorAll('.bundle-view-4-wrapper');
      
      let slideWidth;
      let maxSlide;
      
      if (isView4 && wrappers.length > 0) {
        // View 4: each wrapper is 100% width + gap
        const gap = this.settings.cardGapMobile;
        slideWidth = containerWidth + gap;
        maxSlide = Math.max(0, wrappers.length - 1);
      } else {
        // Normal slider: calculate based on cards visible
        let cardsVisible;
        if (isMobile) {
          cardsVisible = parseInt(this.currentView);
        } else if (isTablet) {
          cardsVisible = this.settings.cardsTablet;
        } else {
          cardsVisible = this.settings.cardsDesktop;
        }
          let gap = isMobile ? this.settings.cardGapMobile : 
                      isTablet ? this.settings.cardGapTablet : 
                      this.settings.cardGapDesktop;
          // Use 8px gap for view 2 on mobile
          if (isMobile && cardsVisible === 2) {
            gap = 8;
          }
          const totalGaps = (cardsVisible - 1) * gap;
          const availableWidth = containerWidth - totalGaps;
          const cardWidth = availableWidth / cardsVisible;
          slideWidth = cardWidth + gap;
        maxSlide = Math.max(0, this.filteredCards.length - cardsVisible);
      }
      
      // Calculate current slide from scroll position
      const currentScrollLeft = this.sliderTrack.scrollLeft;
      const calculatedSlide = Math.round(currentScrollLeft / slideWidth);
      const newSlide = Math.max(0, Math.min(calculatedSlide, maxSlide));
      
      // Only update if slide actually changed
      if (newSlide !== this.currentSlide) {
        this.currentSlide = newSlide;
        this.updateDots();
        this.updateArrows();
      }
    }

    setupMouseDragEvents() {
      if (!this.sliderTrack) return;

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const useTransform = this.useTransformMode;

      const onMouseDown = (e) => {
        if (this.isMobile()) return;
        const target = e.target;
        const isInteractive = target.closest('button, a, input, select, textarea, [data-drawer-trigger]');
        if (isInteractive) {
          this.isDragging = false;
          return;
        }
        
        e.preventDefault();
        this.isDragging = true;
        this.startX = e.pageX - this.sliderTrack.offsetLeft;
        this.currentX = this.startX;
        
        if (useTransform && (this._transformPosition !== undefined)) {
          this._dragStartTransform = this._transformPosition;
        } else {
          this.scrollLeft = this.sliderTrack.scrollLeft;
        }
        
        this.sliderTrack.style.cursor = isSafari ? '-webkit-grabbing' : 'grabbing';
        this.sliderTrack.classList.add('is-dragging');
      };

      const onMouseMove = (e) => {
        if (!this.isDragging || this.isMobile()) return;
        e.preventDefault();
        this.currentX = e.pageX - this.sliderTrack.offsetLeft;
        const walk = (this.currentX - this.startX) * 1.5;
        
        if (useTransform && (this._dragStartTransform !== undefined) && this._slideWidth !== undefined && this._maxSlide !== undefined) {
          const maxTransform = this._maxSlide * this._slideWidth;
          const newTransform = Math.max(0, Math.min(this._dragStartTransform + walk, maxTransform));
          this.sliderContainer.style.transition = 'none';
          this.sliderContainer.style.transform = 'translate3d(-' + newTransform + 'px, 0, 0)';
          this.sliderContainer.style.webkitTransform = 'translate3d(-' + newTransform + 'px, 0, 0)';
        } else {
          this.sliderTrack.scrollLeft = this.scrollLeft - walk;
        }
      };

      const onMouseUp = () => {
        if (!this.isDragging || this.isMobile()) return;
        this.isDragging = false;
        this.sliderTrack.style.cursor = isSafari ? '-webkit-grab' : 'grab';
        this.sliderTrack.classList.remove('is-dragging');

        if (useTransform && this._slideWidth !== undefined && this._maxSlide !== undefined) {
          this.sliderContainer.style.transition = '';
          const walk = (this.currentX - this.startX) * 1.5;
          const currentTransform = this._dragStartTransform + walk;
          const slideIndex = Math.round(currentTransform / this._slideWidth);
          const clamped = Math.max(0, Math.min(slideIndex, this._maxSlide));
          const fromSlide = this.currentSlide;
          this.goToSlide(clamped);
          if (fromSlide !== this.currentSlide) {
            this._pushDebouncedSliderEvent('bundle_slider_drag', {
              direction: fromSlide < this.currentSlide ? 'left' : 'right',
              from_slide: fromSlide,
              to_slide: this.currentSlide,
              device_type: 'desktop'
            });
          }
          return;
        }

        const threshold = 50;
        const diff = this.startX - this.currentX;
        const fromSlide = this.currentSlide;
        if (Math.abs(diff) > threshold) {
          if (diff > 0) {
            this.goToSlide(this.currentSlide + 1);
          } else {
            this.goToSlide(this.currentSlide - 1);
          }
          if (fromSlide !== this.currentSlide) {
            this._pushDebouncedSliderEvent('bundle_slider_drag', {
              direction: diff > 0 ? 'right' : 'left',
              from_slide: fromSlide,
              to_slide: this.currentSlide,
              device_type: 'desktop'
            });
          }
        } else {
          this.updateSlider();
        }
      };

      this.sliderTrack.addEventListener('mousedown', onMouseDown);
      this.sliderTrack.addEventListener('mousemove', onMouseMove);
      this.sliderTrack.addEventListener('mouseleave', onMouseUp);
      document.addEventListener('mouseup', onMouseUp);
    }

    setupFilters() {
      this.filterTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const filter = tab.dataset.filter;
          const min = parseInt(tab.dataset.filterMin) || 0;
          const max = parseInt(tab.dataset.filterMax) || 999;
          const previousFilter = this.currentFilter;
          const tabText = (tab.textContent || '').trim();
          const filterValue = filter || tab.dataset.filterValue || tabText || 'unknown';
          this.applyFilter(filter, min, max);
          trackGaEvent('bundle_filter_apply', {
            filter_value: filterValue,
            filter_min: min,
            filter_max: max,
            previous_filter: previousFilter,
            visible_products_count: this.filteredCards.length,
            section_id: this.sectionId
          });
          // Update tab states
          this.filterTabs.forEach(t => {
            t.classList.remove('bundle-filter-tabs__tab--active');
            t.setAttribute('aria-selected', 'false');
            t.style.background = this.section.dataset.filterTabInactiveBg || 'transparent';
            t.style.color = this.section.dataset.filterTabInactiveText || '#4A4A4A';
            t.style.border = `1px solid ${this.section.dataset.filterTabInactiveBorder || '#E5E5E5'}`;
          });
          
          tab.classList.add('bundle-filter-tabs__tab--active');
          tab.setAttribute('aria-selected', 'true');
          tab.style.background = this.section.dataset.filterTabActiveBg || '#1A1A1A';
          tab.style.color = this.section.dataset.filterTabActiveText || '#FFFFFF';
          tab.style.border = 'none';
        });
      });
    }

    applyFilter(filter, min, max) {
      this.currentFilter = filter;
      
      if (filter === 'all') {
        this.filteredCards = [...this.cards];
      } else {
        this.filteredCards = this.cards.filter(card => {
          // Get team size from data attribute (kebab-case to camelCase conversion)
          const teamSizeAttr = card.getAttribute('data-team-size');
          const teamSize = parseInt(teamSizeAttr) || 0;
          return teamSize >= min && teamSize <= max;
        });
      }

      // Update filter counts
      this.updateFilterCounts(min, max);
      
      // Show/hide cards
      this.cards.forEach(card => {
        const isVisible = this.filteredCards.includes(card);
        card.style.display = isVisible ? '' : 'none';
        if (!isVisible) {
          card.classList.remove('bundle-product-card--visible');
        } else {
          card.classList.add('bundle-product-card--visible');
        }
      });

      // Update slider ready state (prevents flashing narrow cards)
      if (this.sliderContainer) {
        this.sliderContainer.dataset.sliderReady = this.filteredCards.length > 0 ? 'true' : 'false';
      }

      // Reset to first slide after filtering
      this.currentSlide = 0;

      // Reapply view after filtering to preserve view toggle state
      // This will re-wrap cards for view 4 if needed
      if (this.isMobile()) {
        this.applyView(this.currentView);
      }

      // Show empty state if no results (append, never replace - cards must stay in DOM)
      const emptyState = this.section.querySelector('.bundle-empty-state');
      if (this.filteredCards.length === 0) {
        if (!emptyState) {
          const emptyStateEl = document.createElement('div');
          emptyStateEl.className = 'bundle-empty-state';
          emptyStateEl.style.cssText = `
            background: ${this.section.dataset.emptyStateBackground || '#F9FAFB'};
            padding: ${this.section.dataset.emptyStatePadding || '60px 24px'};
            text-align: center;
            width: 100%;
            flex: 0 0 100%;
          `;
          emptyStateEl.innerHTML = `
            <h3 style="color: ${this.section.dataset.emptyStateHeadingColor || '#4A4A4A'};">
              ${this.section.dataset.emptyStateHeading || "No bundles found for this filter"}
            </h3>
            <p style="color: ${this.section.dataset.emptyStateSubheadingColor || '#6B7280'};">
              ${this.section.dataset.emptyStateSubheading || "Try selecting a different team size above"}
            </p>
          `;
          this.sliderContainer.appendChild(emptyStateEl);
        }
      } else {
        if (emptyState) {
          emptyState.remove();
        }
      }

      // Reset to first slide
      this.currentSlide = 0;
      
      // Reapply view after filtering to preserve view toggle state
      if (this.isMobile()) {
        // applyView will call updateSlider for us
        this.applyView(this.currentView);
      } else {
        this.updateSlider();
      }
      
      this.updateDots();
      this.updateArrows();
      this.updateProductCount();
    }

    updateFilterCounts(min, max) {
      // Update counts for all filter tabs
      this.filterTabs.forEach(tab => {
        const countSpan = tab.querySelector('.bundle-filter-tabs__count');
        if (!countSpan) return;
        
        if (tab.dataset.filter === 'all') {
          // For "All" tab, show total number of cards
          countSpan.textContent = `(${this.cards.length})`;
          countSpan.dataset.count = this.cards.length;
        } else {
          // For specific filter tabs, count cards matching the filter range
          const tabMin = parseInt(tab.dataset.filterMin) || 0;
          const tabMax = parseInt(tab.dataset.filterMax) || 999;
          
          const count = this.cards.filter(card => {
            // Get team size from data attribute
            // Try both dataset (camelCase) and getAttribute (kebab-case) methods
            let teamSizeAttr = card.dataset.teamSize;
            if (teamSizeAttr === undefined) {
              teamSizeAttr = card.getAttribute('data-team-size');
            }
            const teamSize = parseInt(teamSizeAttr) || 0;
            
            // Check if team size falls within the filter range
            // Only count if teamSize > 0 (to exclude products without team size set)
            if (teamSize === 0 && tabMin > 0) {
              return false; // Skip products with no team size for non-zero filters
            }
            return teamSize >= tabMin && teamSize <= tabMax;
          }).length;
          
          countSpan.textContent = `(${count})`;
          countSpan.dataset.count = count;
        }
      });
    }

    setupViewToggle() {
      if (!this.viewToggle || !this.isMobile()) return;

      // Update button states to match saved preference
      this.updateButtonStates(this.currentView);

      // Set initial view (this will also save to localStorage if needed)
      this.applyView(this.currentView);

      this.viewToggleButtons.forEach(button => {
        button.addEventListener('click', () => {
          const view = button.dataset.view;
          const previousView = this.currentView;
          this.applyView(view);
          this.updateButtonStates(view);
          trackGaEvent('bundle_view_toggle', {
            view_mode: view || 'unknown',
            previous_view: previousView || 'unknown',
            device_type: 'mobile',
            section_id: this.sectionId
          });
        });
      });
    }
    
    updateButtonStates(activeView) {
      this.viewToggleButtons.forEach(btn => {
        const btnView = btn.dataset.view;
        if (btnView === activeView) {
          btn.classList.add('bundle-view-toggle__button--active');
          btn.style.background = this.section.dataset.viewToggleActiveBg || '#F4F4F4';
          btn.style.borderColor = this.section.dataset.viewToggleActiveBorder || '#D1D5DB';
          btn.style.color = this.section.dataset.viewToggleActiveIcon || '#1A1A1A';
        } else {
          btn.classList.remove('bundle-view-toggle__button--active');
          btn.style.background = 'transparent';
          btn.style.borderColor = this.section.dataset.viewToggleInactiveBorder || '#E5E5E5';
          btn.style.color = this.section.dataset.viewToggleInactiveIcon || '#9CA3AF';
        }
      });
    }

    applyView(view) {
      this.currentView = view;
      
      // Save to localStorage for persistence
      if (this.isMobile() && ['1', '2', '4'].includes(view)) {
        localStorage.setItem('jrs_collection_view_preference', view);
      }
      
      // Update all cards with data-view attribute
      this.filteredCards.forEach(card => {
        card.dataset.view = view;
      });

      // Update slider/grid layout
      if (this.isMobile()) {
        const viewNum = parseInt(view);
        
        // View "4" = paginated 2x2 grid slider
        if (viewNum === 4) {
          // Unwrap any existing wrappers first
          this.unwrapView4Cards();
          
          // Clear inline card sizing from view 1/2 so grid layout can size cards correctly
          this.filteredCards.forEach(card => {
            card.style.flex = '';
            card.style.minWidth = '';
            card.style.maxWidth = '';
          });
          
          // Group filtered cards into sets of 4 and wrap them
          this.wrapView4Cards();
          
          // Ensure container is flex for horizontal sliding
          this.sliderContainer.style.display = 'flex';
          this.sliderContainer.style.gridTemplateColumns = '';
          this.sliderContainer.style.width = '100%';
          
          // Show slider controls for navigation between pages
          if (this.arrows.prev) this.arrows.prev.style.display = '';
          if (this.arrows.next) this.arrows.next.style.display = '';
          if (this.dotsContainer) this.dotsContainer.style.display = '';
          
          // Update slider to handle wrapper-based navigation
          this.updateSlider();
        } else {
          // Unwrap any view 4 wrappers
          this.unwrapView4Cards();
          
          // Switch to normal slider layout
          this.sliderContainer.style.display = 'flex';
          this.sliderContainer.style.gridTemplateColumns = '';
          // Clear container width so natural flex overflow works (non-transform mode)
          this.sliderContainer.style.width = '';
          
          // Show slider controls
          if (this.arrows.prev) this.arrows.prev.style.display = '';
          if (this.arrows.next) this.arrows.next.style.display = '';
          if (this.dotsContainer) this.dotsContainer.style.display = '';
          
          this.updateSlider();
        }
      }
    }
    
    wrapView4Cards() {
      if (!this.sliderContainer || this.filteredCards.length === 0) return;
      
      // Group cards into sets of 4
      const cardsPerPage = 4;
      const pageCount = Math.ceil(this.filteredCards.length / cardsPerPage);
      
      for (let i = 0; i < pageCount; i++) {
        const wrapper = document.createElement('div');
        wrapper.className = 'bundle-view-4-wrapper';
        wrapper.dataset.pageIndex = i;
        
        // Get cards for this page
        const startIdx = i * cardsPerPage;
        const endIdx = Math.min(startIdx + cardsPerPage, this.filteredCards.length);
        const pageCards = this.filteredCards.slice(startIdx, endIdx);
        
        // Move cards into wrapper
        pageCards.forEach(card => {
          wrapper.appendChild(card);
        });
        
        // Append wrapper to container
        this.sliderContainer.appendChild(wrapper);
      }
    }
    
    unwrapView4Cards() {
      if (!this.sliderContainer) return;
      
      // Find all view 4 wrappers
      const wrappers = this.sliderContainer.querySelectorAll('.bundle-view-4-wrapper');
      
      if (wrappers.length === 0) return;
      
      // Move cards back to container
      wrappers.forEach(wrapper => {
        while (wrapper.firstChild) {
          this.sliderContainer.insertBefore(wrapper.firstChild, wrapper);
        }
        wrapper.remove();
      });
    }

    setupDrawers() {
      setupDrawersForCards(this.filteredCards, {
        animationDuration: this.settings.animationDuration,
        shouldAnimate: this.shouldAnimate,
        isMobile: this.isMobile()
      });
    }

    closeAllDrawers() {
      closeAllDrawersModule();
    }

    markSliderReady() {
      if (!this.sliderContainer) return;
      if (this.filteredCards.length === 0) {
        this.sliderContainer.dataset.sliderReady = 'false';
        return;
      }
      this.sliderContainer.dataset.sliderReady = 'true';
      if (this.sliderTrack && !this.isMobile()) {
        this.sliderTrack.style.cursor = 'grab';
      }
    }

    getSliderWidth() {
      if (!this.sliderTrack) return 0;

      const directWidth = this.sliderTrack.offsetWidth;
      const rectWidth = this.sliderTrack.getBoundingClientRect().width;
      let width = directWidth > 0 ? directWidth : (rectWidth > 0 ? rectWidth : 0);

      if (width <= 0) {
        const parent = this.sliderTrack.parentElement;
        if (parent) {
          const parentOffset = parent.offsetWidth;
          const parentRectWidth = parent.getBoundingClientRect().width;
          width = parentOffset > 0 ? parentOffset : (parentRectWidth > 0 ? parentRectWidth : 0);
        }
      }
      if (width <= 0) {
        const sectionWidth = this.section.offsetWidth;
        const sectionRectWidth = this.section.getBoundingClientRect().width;
        width = sectionWidth > 0 ? sectionWidth : (sectionRectWidth > 0 ? sectionRectWidth : 0);
      }
      if (width <= 0) {
        width = window.innerWidth || 0;
      }

      // On mobile, cap to viewport so we never use a stale/desktop width (e.g. iOS Chrome when saved view 1/2 from cookie)
      if (this.isMobile() && width > 0) {
        const viewportW = (typeof window.visualViewport !== 'undefined' && window.visualViewport.width > 0)
          ? window.visualViewport.width
          : window.innerWidth;
        if (viewportW > 0 && width > viewportW) {
          width = viewportW;
        }
        // Guard against suspiciously small widths on mobile (likely measurement before layout)
        if (width < 200) {
          return 0; // Defer to requestAnimationFrame retry
        }
      }

      return width;
    }

    setupSlider() {
      // Just call updateSlider - it has the correct logic for all breakpoints
      this.updateSlider();
      this.markSliderReady();
    }

    updateSlider() {
      if (this.filteredCards.length === 0) return;

      const isMobile = this.isMobile();
      const isTablet = this.isTablet();
      
      // Check if we're in view 4 mode (paginated 2x2 grid)
      const isView4 = isMobile && parseInt(this.currentView) === 4;
      const wrappers = this.sliderContainer.querySelectorAll('.bundle-view-4-wrapper');
      
      if (isView4 && wrappers.length > 0) {
        // View 4: Navigate between wrapper pages
        const containerWidth = this.getSliderWidth();
        if (containerWidth <= 0) {
          window.requestAnimationFrame(() => this.updateSlider());
          return;
        }
        
        const gap = this.settings.cardGapMobile;
        
        // Reset container
        this.sliderContainer.style.width = '';
        this.sliderContainer.style.gap = `${gap}px`;
        
        // Each wrapper is 100% width
        wrappers.forEach(wrapper => {
          wrapper.style.flex = '0 0 100%';
          wrapper.style.minWidth = '100%';
          wrapper.style.maxWidth = '100%';
        });
        
        const maxSlide = Math.max(0, wrappers.length - 1);
        this.currentSlide = Math.min(this.currentSlide, maxSlide);
        
        // Calculate scroll position based on wrapper index
        const scrollPosition = this.currentSlide * (containerWidth + gap);
        
        if (this.useTransformMode) {
          const totalWidth = wrappers.length * containerWidth + (wrappers.length - 1) * gap;
          const stepWidth = containerWidth + gap;
          this._transformPosition = scrollPosition;
          this._slideWidth = stepWidth;
          this._maxSlide = maxSlide;
          this.sliderContainer.style.width = totalWidth + 'px';
          wrappers.forEach(function(w) {
            w.style.flex = '0 0 ' + containerWidth + 'px';
            w.style.minWidth = containerWidth + 'px';
            w.style.maxWidth = containerWidth + 'px';
          });
          this.sliderContainer.style.transform = 'translate3d(-' + scrollPosition + 'px, 0, 0)';
          this.sliderContainer.style.webkitTransform = 'translate3d(-' + scrollPosition + 'px, 0, 0)';
        } else {
          if (this.shouldAnimate) {
            this.sliderTrack.style.scrollBehavior = 'smooth';
          } else {
            this.sliderTrack.style.scrollBehavior = 'auto';
          }
          this.sliderTrack.scrollLeft = scrollPosition;
        }
        
        this.markSliderReady();
        return;
      }
      
      // Normal slider logic for views 1 and 2
      let cardsVisible;
      if (isMobile) {
        cardsVisible = parseInt(this.currentView);
      } else if (isTablet) {
        cardsVisible = this.settings.cardsTablet;
      } else {
        cardsVisible = this.settings.cardsDesktop;
      }

      const viewNum = isMobile ? parseInt(this.currentView) : null;
      // For view 2 on mobile, use 8px gap instead of default
      const gap = isMobile ? (viewNum === 2 ? 8 : this.settings.cardGapMobile) :
                  isTablet ? this.settings.cardGapTablet : 
                  this.settings.cardGapDesktop;

      const containerWidth = this.getSliderWidth();
      if (containerWidth <= 0) {
        window.requestAnimationFrame(() => this.updateSlider());
        return;
      }

      let peekAmount = 0;
      if (isTablet) {
        peekAmount = this.settings.enablePeek ? this.settings.peekAmount : 0;
      }
      const totalGaps = (cardsVisible - 1) * gap;
      const peekWidth = peekAmount > 0 ? (containerWidth * peekAmount / 100) : 0;
      const availableWidth = containerWidth - totalGaps - peekWidth;
      const cardWidthPx = availableWidth / cardsVisible;

      const gapForScroll = gap;
      
      // Calculate container width FIRST
      const slideWidth = cardWidthPx + gapForScroll;
      const totalWidth = this.filteredCards.length * cardWidthPx + (this.filteredCards.length - 1) * gapForScroll;
      const scrollPosition = this.currentSlide * slideWidth;
      
      // Set gap
      this.sliderContainer.style.gap = `${gap}px`;
      
      // Container width: Only set in transform mode (needed for translate3d)
      // In non-transform mode, clear width to allow natural flex overflow
      if (this.useTransformMode) {
        this.sliderContainer.style.width = totalWidth + 'px';
      } else {
        this.sliderContainer.style.width = '';
      }

      // Apply card sizing: Use pixel-based widths for ALL modes
      // This ensures consistent sizing regardless of transform mode
      this.filteredCards.forEach(card => {
        card.style.flex = `0 0 ${cardWidthPx}px`;
        card.style.minWidth = `${cardWidthPx}px`;
        card.style.maxWidth = `${cardWidthPx}px`;
        card.style.width = `${cardWidthPx}px`;
        card.style.flexShrink = '0';
        card.style.flexGrow = '0';
      });

      const maxSlide = Math.max(0, this.filteredCards.length - cardsVisible);
      this.currentSlide = Math.min(this.currentSlide, maxSlide);
      
      if (this.useTransformMode) {
        this._transformPosition = scrollPosition;
        this._slideWidth = cardWidthPx + gapForScroll;
        this._maxSlide = maxSlide;
        this.sliderContainer.style.transform = 'translate3d(-' + scrollPosition + 'px, 0, 0)';
        this.sliderContainer.style.webkitTransform = 'translate3d(-' + scrollPosition + 'px, 0, 0)';
      } else {
        // Non-transform mode (Chrome, Firefox, etc.): use scroll
        if (this.shouldAnimate) {
          this.sliderTrack.style.scrollBehavior = 'smooth';
        } else {
          this.sliderTrack.style.scrollBehavior = 'auto';
        }
        const track = this.sliderTrack;
        const pos = scrollPosition;
        track.scrollLeft = pos;
        requestAnimationFrame(() => {
          track.scrollLeft = pos;
          if (Math.round(track.scrollLeft) !== Math.round(pos)) {
            requestAnimationFrame(() => { track.scrollLeft = pos; });
          }
        });
      }

      this.markSliderReady();
      
      // Update card heights after slider layout is complete
      var self = this;
      setTimeout(function() {
        self.updateCardHeights();
      }, 50);
    }

    goToSlide(index) {
      if (this.filteredCards.length === 0) {
        console.warn('[Featured Bundles] goToSlide called but no filtered cards');
        return;
      }

      const isMobile = this.isMobile();
      const isTablet = this.isTablet();
      const isView4 = isMobile && parseInt(this.currentView) === 4;
      const wrappers = this.sliderContainer.querySelectorAll('.bundle-view-4-wrapper');
      
      let maxSlide;
      if (isView4 && wrappers.length > 0) {
        maxSlide = Math.max(0, wrappers.length - 1);
      } else {
        let cardsVisible;
        if (isMobile) {
          cardsVisible = parseInt(this.currentView);
        } else if (isTablet) {
          cardsVisible = this.settings.cardsTablet;
        } else {
          cardsVisible = this.settings.cardsDesktop;
        }
        maxSlide = Math.max(0, this.filteredCards.length - cardsVisible);
      }
      
      if (this.settings.enableLoop) {
        if (index < 0) {
          index = maxSlide;
        } else if (index > maxSlide) {
          index = 0;
        }
      } else {
        index = Math.max(0, Math.min(index, maxSlide));
      }

      this.currentSlide = index;
      this.updateSlider();
      this.updateDots();
      this.updateArrows();
    }

    updateDots() {
      if (!this.dotsContainer) {
        console.warn('[Featured Bundles] Dots container not found!');
        return;
      }

      const isMobile = this.isMobile();
      const isTablet = this.isTablet();
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      // Check if we're in view 4 mode
      const isView4 = isMobile && parseInt(this.currentView) === 4;
      const wrappers = this.sliderContainer.querySelectorAll('.bundle-view-4-wrapper');
      
      let totalSlides;
      if (isView4 && wrappers.length > 0) {
        // View 4: Each dot represents one 2×2 grid page
        totalSlides = wrappers.length;
        this.dotsContainer.style.display = '';
      } else {
        // Normal slider logic
        let cardsVisible;
        if (isMobile) {
          cardsVisible = parseInt(this.currentView);
        } else if (isTablet) {
          cardsVisible = this.settings.cardsTablet;
        } else {
          cardsVisible = this.settings.cardsDesktop;
        }
        totalSlides = Math.ceil(this.filteredCards.length / cardsVisible);
      }

      this.dotsContainer.innerHTML = '';

      for (let i = 0; i < totalSlides; i++) {
        const dot = document.createElement('button');
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', i === this.currentSlide ? 'true' : 'false');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('tabindex', i === this.currentSlide ? '0' : '-1');
        dot.classList.add('featured-collection-bundles__dot');
        
        if (i === this.currentSlide) {
          dot.classList.add('featured-collection-bundles__dot--active');
        }

        // Safari-specific: Add touch event support for dots
        const handleDotClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const fromSlide = this.currentSlide;
          this.currentSlide = i;
          this.updateSlider();
          this.updateDots();
          this.updateArrows();
          if (window.jrsTrackEvent) {
            window.jrsTrackEvent('bundle_slider_dot_click', {
              target_slide: i,
              from_slide: fromSlide,
              section_id: this.sectionId
            }, { prefix: '' });
          }
        };

        dot.addEventListener('click', handleDotClick, { passive: false });
        
        // Safari-specific: Add touch support for better mobile responsiveness
        if (isSafari || isMobile) {
          dot.addEventListener('touchstart', handleDotClick, { passive: false });
        }

        // Dot style
        const dotStyle = this.settings.dotStyle || 'circles';
        if (dotStyle === 'circles') {
          dot.innerHTML = i === this.currentSlide ? '●' : '○';
        } else if (dotStyle === 'bars') {
          dot.innerHTML = i === this.currentSlide ? '▬' : '░';
        } else if (dotStyle === 'numbers') {
          dot.textContent = i + 1;
        }

        this.dotsContainer.appendChild(dot);
      }
    }

    updateArrows() {
      if (!this.arrows.prev || !this.arrows.next) return;

      const isMobile = this.isMobile();
      const isTablet = this.isTablet();
      
      // Check if we're in view 4 mode
      const isView4 = isMobile && parseInt(this.currentView) === 4;
      const wrappers = this.sliderContainer.querySelectorAll('.bundle-view-4-wrapper');
      
      // Keep arrows visible for view 4
      this.arrows.prev.style.display = '';
      this.arrows.next.style.display = '';
      
      let maxSlide;
      if (isView4 && wrappers.length > 0) {
        // View 4: Navigate between wrapper pages
        maxSlide = Math.max(0, wrappers.length - 1);
      } else {
        // Normal slider logic
        let cardsVisible;
        if (isMobile) {
          cardsVisible = parseInt(this.currentView);
        } else if (isTablet) {
          cardsVisible = this.settings.cardsTablet;
        } else {
          cardsVisible = this.settings.cardsDesktop;
        }
        maxSlide = Math.max(0, this.filteredCards.length - cardsVisible);
      }

      const isAtStart = this.currentSlide === 0;
      const isAtEnd = this.currentSlide >= maxSlide;

      if (!this.settings.enableLoop) {
        this.arrows.prev.disabled = isAtStart;
        this.arrows.prev.setAttribute('aria-disabled', isAtStart);
        this.arrows.prev.style.opacity = isAtStart ? '0.4' : '1';
        this.arrows.prev.style.cursor = isAtStart ? 'not-allowed' : 'pointer';

        this.arrows.next.disabled = isAtEnd;
        this.arrows.next.setAttribute('aria-disabled', isAtEnd);
        this.arrows.next.style.opacity = isAtEnd ? '0.4' : '1';
        this.arrows.next.style.cursor = isAtEnd ? 'not-allowed' : 'pointer';
      }
    }

    updateProductCount() {
      const countElement = this.section.querySelector('.featured-collection-bundles__product-count');
      if (!countElement) return;

      const count = this.filteredCards.length;
      let format = this.section.dataset.productCountFormat || 'Showing COUNT bundles';
      // Decode HTML entities if present
      format = format.replace(/&#123;/g, '{').replace(/&#125;/g, '}');
      // Replace both {count} and COUNT placeholders
      format = format.replace(/\{count\}/g, count).replace(/COUNT/g, count);
      countElement.textContent = format;
    }

    startAutoplay() {
      if (!this.settings.enableAutoplay) return;
      
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.goToSlide(this.currentSlide + 1);
      }, this.settings.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    isMobile() {
      return window.innerWidth <= this.breakpoints.mobile;
    }

    isTablet() {
      return window.innerWidth > this.breakpoints.mobile && window.innerWidth <= this.breakpoints.tablet;
    }
  }

  // Initialize all sections and grid bundle card drawers
  document.addEventListener('DOMContentLoaded', () => {
    const sections = document.querySelectorAll('.featured-collection-bundles');
    sections.forEach(section => {
      new FeaturedCollectionBundles(section);
    });
    initBundleCardDrawersForGrid();

    // Single global listener: close any open drawer when clicking outside a bundle card
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.bundle-product-card')) {
        var openDrawer = document.querySelector('.bundle-card-drawer--open');
        var productId = '';
        if (openDrawer) {
          var cardEl = openDrawer.closest('.bundle-product-card');
          productId = cardEl ? cardEl.getAttribute('data-product-id') || '' : '';
        }
        closeAllDrawersModule();
        if (window.jrsTrackEvent && productId) {
          window.jrsTrackEvent('bundle_drawer_close', {
            product_id: productId,
            close_method: 'outside_click'
          }, { prefix: '' });
        }
      }
    });
  });

  // Re-initialize on section load (for Shopify theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector('.featured-collection-bundles');
      if (section) {
        new FeaturedCollectionBundles(section);
      }
    });
  }
})();
